import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './AcceptableUsePolicy.module.css';

const AcceptableUsePolicy = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  // prettier-ignore
  return (
    <div className={classes}>

      <strong className={css.lastUpdated}>Last updated: 3 December, 2025</strong>
      <p>This Acceptable Use Policy ("<strong>AUP</strong>") sets out the rules and standards for using the BOLDLY Services. It applies to all users, including Customers, Authorised Users, coaches, clients and any other individuals interacting with our platform.</p>
      <p>By accessing or using the Services, you agree to comply with this AUP, our User Terms of Service, and any other policies referenced therein.</p>
      <p>If we determine that conduct violates this AUP—especially if deliberate, repeated, harmful or unlawful—we may suspend, restrict or terminate access to the Services.</p>
      <p>Capitalised terms used in this AUP have the meanings given in the <strong>User Terms of Service.</strong></p>

      <h2>1. General Conduct Expectations</h2>
      <p>All users must:</p>
      <ul>
        <li>act professionally and respectfully toward other users</li>
        <li>comply with all applicable laws and regulations (including privacy, IP, discrimination, harassment and employment laws)</li>
        <li>use the Services only for lawful, intended purposes</li>
        <li>protect the confidentiality of Customer Data, User Content and personal information</li>
        <li>maintain the security of your account credentials</li>
        <li>immediately report suspicious, harmful or unauthorised activity to: <a href="mailto:connect@boldly.app">connect@boldly.app</a></li>
      </ul>

      <h2>2. What You MUST Do</h2>
      <p>You must:</p>

      <h3>2.1 Comply with Legal and Contractual Obligations</h3>
      <ul>
        <li>follow the <strong>User Terms of Service</strong>, this AUP and the <strong>Privacy Policy</strong></li>
        <li>comply with all applicable Australian laws and global privacy, intellectual property and data protection regulations</li>
        <li>comply with contractual obligations to Customers, employers or sponsoring organisations</li>
      </ul>

      <h3>2.2 Handle Data Responsibly</h3>
      <ul>
        <li>upload or share only Customer Data or User Content that you have the right to use</li>
        <li>respect intellectual property, confidentiality and privacy rights</li>
        <li>use commercially reasonable efforts to prevent unauthorised access or disclosure</li>
        <li>ensure information shared in coaching engagements complies with professional conduct guidelines</li>
      </ul>

      <h3>2.3 Maintain Account Security</h3>
      <ul>
        <li>keep passwords and login credentials confidential</li>
        <li>monitor activity occurring under your account</li>
        <li>notify BOLDLY immediately of suspected security breaches, loss of credentials or unauthorised access</li>
      </ul>

      <h3>2.4 Follow Integrations and API Terms</h3>
      <ul>
        <li>comply with third-party application terms when integrating them into the Services</li>
      </ul>

      <h2>3. What You MUST NOT Do</h2>
      <p>You must NOT, under any circumstances:</p>

      <h3>3.1 Unauthorised Use & Account Misuse</h3>
      <ul>
        <li>allow non-Authorised Users to access the Services using your credentials</li>
        <li>share, sell, transfer or assign your account to another person</li>
        <li>create accounts via automation, bots or scripts</li>
        <li>access the Services in any manner other than our supported interfaces</li>
        <li>impersonate any individual or organisation, including a coach, client, BOLDLY employee or Customer administrator</li>
      </ul>

      <h3>3.2 Intellectual Property & Content Violations</h3>
      <p>You must NOT upload, store, transmit or share content that:</p>
      <ul>
        <li>infringes copyrights, trademarks or other IP rights</li>
        <li>is confidential information belonging to others without authorisation</li>
        <li>violates privacy rights or confidentiality obligations</li>
        <li>is misleading, defamatory, abusive, fraudulent or unlawful</li>
        <li>is harmful, discriminatory, harassing, sexually explicit or exploitative</li>
        <li>targets or harms individuals under 18 years old</li>
        <li>includes malware, viruses, worms or harmful code</li>
        <li>includes deepfakes, manipulated media, or unauthorised recordings of coaching sessions</li>
      </ul>

      <h3>3.3 Interference, Security Violations & Platform Abuse</h3>
      <p>You must NOT:</p>
      <ul>
        <li>attempt to hack, disable, disrupt, reverse engineer or decompile the Services</li>
        <li>bypass or circumvent security controls or authentication</li>
        <li>attempt unauthorised access to BOLDLY systems, networks or data</li>
        <li>attack the platform with automated scraping, crawling or data extraction</li>
        <li>build a competitive product using access to the Services</li>
        <li>use the Services for surveillance, monitoring or profiling individuals without consent</li>
        <li>upload or link to harmful code or technologies designed to access data unlawfully</li>
      </ul>

      <h3>3.4 Misuse of Marketplace Features</h3>
      <p>You must NOT:</p>
      <ul>
        <li>manipulate reviews, ratings or client feedback</li>
        <li>solicit clients outside the platform using information obtained through the Services, unless expressly permitted</li>
        <li>harvest email addresses, contact details or profile information</li>
        <li>abuse referral, promotional or credit systems</li>
        <li>advertise services inside Customer accounts unless authorised</li>
        <li>engage in spam, unsolicited promotions or bulk messaging</li>
      </ul>

      <h3>3.5 Illegal or Prohibited Activities</h3>
      <p>You must NOT use the Services to:</p>
      <ul>
        <li>conduct or enable unlawful activity</li>
        <li>support, promote or distribute extremist, violent or terrorist content</li>
        <li>stalk, threaten, harass or harm individuals</li>
        <li>target vulnerable groups or minors</li>
        <li>discriminate on the basis of race, gender, ethnicity, disability, age, religion, sexual orientation, or any protected attribute</li>
      </ul>

      <h2>4. Additional Standards for Coaches</h2>
      <p>Coaches using the platform must NOT:</p>
      <ul>
        <li>breach client confidentiality except as required by law or according to safeguarding obligations</li>
        <li>record coaching sessions without explicit consent</li>
        <li>provide coaching outside their competency or accreditation</li>
        <li>offer medical, legal or financial advice unless licensed and engaged for such services</li>
        <li>solicit clients for services unrelated to coaching without clear consent</li>
        <li>misuse Customer Data shared through enterprise programs</li>
      </ul>
      <p>Coaches must uphold professional standards consistent with their training bodies (ICF, EMCC, BPS, etc.).</p>

      <h2>5. Additional Standards for Clients</h2>
      <p>Clients must NOT:</p>
      <ul>
        <li>share personal details, confidential documents or sensitive information of third parties without consent</li>
        <li>behave abusively or disrespectfully towards coaches</li>
        <li>attempt to solicit free coaching or circumvent session limits</li>
        <li>submit inaccurate or misleading booking details or assessments</li>
      </ul>

      <h2>6. Enforcement, Suspension & Termination</h2>
      <p>We may take any action deemed appropriate, including:</p>
      <ul>
        <li>issuing warnings</li>
        <li>removing or restricting content</li>
        <li>suspending or terminating accounts</li>
        <li>notifying the Customer or employer (where applicable)</li>
        <li>cooperating with law enforcement if required</li>
        <li>pursuing legal remedies</li>
      </ul>
      <p>We consider intentional, repeated or high-risk violations to be serious breaches.</p>

      <h2>7. Reporting Violations</h2>
      <p>If you believe someone is violating this AUP or engaging in harmful behaviour, please report it promptly:</p>
      <p><a href="mailto:connect@boldly.app">connect@boldly.app</a></p>
      <p>Reports may remain confidential.</p>

      <h2>8. Changes to This Policy</h2>
      <p>We may update this AUP from time to time. Changes will be posted on our website and will take effect when published. Continued use of the Services constitutes acceptance of any updates.</p>

      <h2>9. Contact Information</h2>
      <p>For questions about this Acceptable Use Policy:</p>
      <p>
        <strong>Bendelta Pty Ltd</strong> (trading as BOLDLY)<br/>
        Level 5, 32–36 York Street<br/>
        Sydney NSW 2000, Australia<br/>
        Phone: <a href="tel:+61292323401">+61 2 9232 3401</a><br/>
        Email: <a href="mailto:connect@boldly.app">connect@boldly.app</a>
      </p>

    </div>
  );
};

const { string } = PropTypes;

AcceptableUsePolicy.defaultProps = {
  rootClassName: null,
  className: null,
};

AcceptableUsePolicy.propTypes = {
  rootClassName: string,
  className: string,
};

export default AcceptableUsePolicy;
